/**
 * Type definitions for Spotlight Basic Setup (Step 1) structures
 * Replaces loose 'any' types with proper TypeScript interfaces
 */

export type PhotoStatus = 'pending' | 'committed'
export type PhotoFitMode = 'cover' | 'contain'
export type PhotoGradientSide = 'left' | 'right'
export type CTAType = 'book_call'
export type DeliveryType = '1:1 sessions' | 'Program' | 'Group' | 'Other' | 'One-time purchase' | 'Subscription' | 'Bundle'
export type TimeframeType = '2 weeks' | '4 weeks' | '6 weeks' | '8 weeks' | '12 weeks' | 'Ongoing'

export interface Location {
  city?: string
  country?: string
}

export interface BasicInfo {
  name: string
  who_you_help?: string
  headline?: string
  offer_primary_outcome?: string
  offer_timeframe?: string
  offer_delivery?: string
  offer_exclusions?: string[]
  carousel_bg_palette?: string
  photo_url?: string
  photo_status?: PhotoStatus
  photo_storage_path?: string
  photo_cutout_url?: string
  photo_focal_x?: number
  photo_focal_y?: number
  photo_scale?: number
  photo_fit_mode?: PhotoFitMode
  photo_gradient_side?: PhotoGradientSide
  photo_reveal?: number
  cta_type?: CTAType
  specialty?: string
  location?: Location
  client_types?: string[]
  tagline?: string
  website_url?: string
  booking_url?: string
  logo_url?: string
}

export interface PhotoUploadResult {
  photo_url: string
  storage_path: string
  status: PhotoStatus
  photo_cutout_url?: string
}

// Field validation types
export type BasicSetupField = 'name' | 'who_you_help' | 'offer_primary_outcome' | 'photo_url'

export interface ValidationResult {
  isValid: boolean
  missingFields: BasicSetupField[]
}

// Helper function to validate basic setup form
export const validateBasicSetup = (basicInfo: BasicInfo | null): ValidationResult => {
  if (!basicInfo) {
    return {
      isValid: false,
      missingFields: ['name', 'who_you_help', 'offer_primary_outcome', 'photo_url']
    }
  }

  const missingFields: BasicSetupField[] = []

  if (!basicInfo.name?.trim()) missingFields.push('name')
  if (!basicInfo.who_you_help?.trim()) missingFields.push('who_you_help')
  if (!basicInfo.offer_primary_outcome?.trim()) missingFields.push('offer_primary_outcome')
  if (!basicInfo.photo_url) missingFields.push('photo_url')

  return {
    isValid: missingFields.length === 0,
    missingFields
  }
}

// Helper function to get user-friendly field name
export const getFieldLabel = (field: BasicSetupField, isProduct: boolean = false): string => {
  switch (field) {
    case 'name':
      return isProduct ? 'Brand or product name' : 'Name'
    case 'who_you_help':
      return 'Who is this for?'
    case 'offer_primary_outcome':
      return isProduct ? 'What change does it help them achieve?' : 'What change do you help them achieve?'
    case 'photo_url':
      return isProduct ? 'Image' : 'Photo'
    default:
      return field
  }
}

// Helper function to get user-friendly photo status
export const getPhotoStatusMessage = (status: PhotoStatus | undefined): string => {
  switch (status) {
    case 'committed':
      return 'Photo uploaded and processed'
    case 'pending':
      return 'Photo uploaded (processing...)'
    default:
      return 'No photo uploaded'
  }
}

// Helper function to check if file is valid
export const validatePhotoFile = (file: File): { valid: boolean; error?: string; warning?: string } => {
  const normalizedType = (file.type || '').toLowerCase()
  const fileName = file.name.toLowerCase()
  
  // Check for HEIC/HEIF files (iPhone default format)
  const isHeic = normalizedType.includes('heic') || 
                 normalizedType.includes('heif') ||
                 fileName.endsWith('.heic') ||
                 fileName.endsWith('.heif')
  
  if (isHeic) {
    return { 
      valid: false, 
      error: 'HEIC format is not supported. Please convert your photo to JPEG. On iPhone: Settings > Camera > Formats > Most Compatible, or select "Automatic" when uploading.' 
    }
  }

  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']
  if (!allowedTypes.includes(normalizedType)) {
    // Explicitly reject common unsupported image formats
    const unsupportedTypes = ['image/gif', 'image/webp', 'image/svg+xml', 'image/bmp']
    if (unsupportedTypes.includes(normalizedType)) {
      return { valid: false, error: 'Only JPG or PNG images are allowed' }
    }
    
    // Check if it's an image file with unknown type (might be converted by iOS)
    if (normalizedType.startsWith('image/')) {
      // iOS Safari sometimes converts HEIC to JPEG but file.type might be empty or generic
      // Try to proceed if it looks like an image
      return { 
        valid: true, 
        warning: 'Unknown image format detected. If upload fails, please convert to JPEG or PNG.' 
      }
    }
    return { valid: false, error: 'Only JPG or PNG images are allowed' }
  }

  // Hard upper limit to prevent abuse (100MB)
  const absoluteMaxSize = 100 * 1024 * 1024 // 100MB
  if (file.size > absoluteMaxSize) {
    return { valid: false, error: 'File is too large. Maximum size is 100MB.' }
  }

  // Warn about large files but allow them (compression will handle it)
  const recommendedMaxSize = 5 * 1024 * 1024 // 5MB
  if (file.size > recommendedMaxSize) {
    return { 
      valid: true, 
      warning: 'Large file detected. It will be automatically compressed before upload.' 
    }
  }

  return { valid: true }
}

// Type guard for BasicInfo
export const isBasicInfoComplete = (info: Partial<BasicInfo>): info is BasicInfo => {
  return !!(
    info.name?.trim() &&
    info.who_you_help?.trim() &&
    info.offer_primary_outcome?.trim() &&
    info.photo_url
  )
}
